import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Rocket, TrendUp, Bank, ChartLineUp, Globe, Handshake, Sparkle,
  CheckCircle, ArrowRight, FileText, Spinner, XCircle, WarningCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import DeliverableRender from "@/components/DeliverableRender";

const ICON_MAP = {
  rocket: Rocket,
  trending: TrendUp,
  building: Bank,
  "chart-up": ChartLineUp,
  globe: Globe,
  handshake: Handshake,
};
const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function WorkflowRun() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loadError, setLoadError] = useState(null); // {status, message}
  const pollRef = useRef(null);
  const consecutiveFailsRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/workflows/${runId}`);
      setRun(data);
      setLoadError(null);
      consecutiveFailsRef.current = 0;
      if (data.deliverables && data.deliverables.length > 0) {
        setActiveId((prev) => prev || data.deliverables[data.deliverables.length - 1].id);
      }
      if (data.status === "completed" || data.status === "failed") {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (e) {
      const status = e.response?.status;
      // Hard fail: 401/403/404 — stop polling and surface error immediately
      if (status === 401 || status === 403 || status === 404) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        const message =
          status === 404
            ? "This workflow run does not exist or has been deleted."
            : status === 403
            ? "You do not have permission to view this workflow run."
            : "Your session has expired. Please sign in again.";
        setLoadError({ status, message });
        return;
      }
      // Transient (5xx, network): tolerate a few failures, then surface
      consecutiveFailsRef.current += 1;
      if (consecutiveFailsRef.current >= 3 && !run) {
        setLoadError({
          status: status || 0,
          message: "Unable to reach the workflow service. Check your connection and retry.",
        });
      }
    }
  }, [runId, run]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [runId, load]);

  const handleRetry = () => {
    setLoadError(null);
    consecutiveFailsRef.current = 0;
    load();
    if (!pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    }
  };

  // ---- Loading state ----
  if (!run && !loadError) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl" data-testid="workflow-run-loading" role="status" aria-live="polite">
        <div className="text-overline text-muted-foreground">Workflow run</div>
        <div className="mt-4 flex items-center gap-3">
          <Spinner size={20} weight="bold" className="animate-spin text-primary" />
          <div className="font-display text-2xl tracking-tight">Loading workflow run…</div>
        </div>
        <div className="mt-2 text-sm text-muted-foreground">Reconciling intelligence pipeline state.</div>
      </div>
    );
  }

  // ---- Error state ----
  if (loadError) {
    return (
      <div className="p-8 lg:p-12 max-w-2xl" data-testid="workflow-run-error" role="alert">
        <Link to="/workflows" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="workflow-run-error-back">
          ← All workflow runs
        </Link>
        <div className="mt-6 inline-flex items-center gap-2 text-overline text-rose-600">
          <WarningCircle size={14} weight="bold" /> Workflow unavailable
        </div>
        <h1 className="font-display text-3xl tracking-tight mt-2">{loadError.message}</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-md">
          The rest of Orixa Invest is still working. You can retry, return to your workflow runs, or
          jump back to the dashboard.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90"
            data-testid="workflow-run-retry"
          >
            <ArrowsClockwise size={14} weight="bold" /> Retry
          </button>
          <Link
            to="/workflows"
            className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
          >
            All workflow runs
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground px-2 self-center"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const Icon = ICON_MAP[run.icon] || Sparkle;
  const accent = ACCENT_MAP[run.accent] || ACCENT_MAP.navy;
  const steps = run.steps || [];
  const deliverables = run.deliverables || [];
  const completed = steps.filter((s) => s.status === "completed").length;
  const total = steps.length;
  const active = deliverables.find((d) => d.id === activeId);

  let statusColor = "text-foreground";
  if (run.status === "completed") statusColor = "text-teal";
  else if (run.status === "failed") statusColor = "text-rose-500";

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="workflow-run-page">
      <Link to={`/workspaces/${run.workspace_id}`} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        ← Back to workspace
      </Link>

      <div className="flex items-start gap-4 mt-4 mb-8 flex-wrap" data-testid="workflow-run-header">
        <div className={`h-14 w-14 rounded-xl grid place-items-center ${accent}`}>
          <Icon size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-overline text-primary mb-1">Workflow run</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight" data-testid="run-title">{run.label}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Status: <span className={"font-semibold " + statusColor}>{run.status}</span>
            {" · "}{completed}/{total} deliverables
            {run.current_step && run.status !== "completed" ? " · " + run.current_step : null}
          </p>
        </div>

        {run.status === "completed" && run.next_action_label && run.next_recommended && (
          <Link
            to={`/workflows/${run.next_recommended}?workspace=${run.workspace_id}`}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 card-elev"
            data-testid="next-action-cta"
          >
            {run.next_action_label} <ArrowRight weight="bold" size={14} />
          </Link>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 mb-8" data-testid="workflow-progress">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="text-overline">Progress</span>
          <span className="font-mono font-semibold">{run.progress}%</span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <div
            className={"h-full transition-all duration-500 " + (run.status === "failed" ? "bg-rose-500" : "bg-primary")}
            style={{ width: run.progress + "%" }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="text-overline text-muted-foreground mb-3">Steps & deliverables</div>
          <ol className="space-y-2" data-testid="workflow-steps-list">
            {steps.map((s, i) => {
              const deliverable = deliverables.find((d) => d.step_key === s.key);
              const isActive = active && active.step_key === s.key;
              const isCompleted = s.status === "completed";
              const isFailed = s.status === "failed";
              const stepClasses =
                "w-full text-left border rounded-lg p-3.5 transition-colors flex items-start gap-3 " +
                (isActive ? "border-primary bg-teal-soft" : "border-border hover:bg-card") +
                (!deliverable ? " opacity-70 cursor-default" : "");
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    disabled={!deliverable}
                    onClick={() => deliverable && setActiveId(deliverable.id)}
                    className={stepClasses}
                    data-testid={`step-${s.key}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isCompleted ? (
                        <CheckCircle size={18} weight="fill" className="text-teal" />
                      ) : isFailed ? (
                        <XCircle size={18} weight="fill" className="text-rose-500" />
                      ) : (
                        <div className="h-[18px] w-[18px] rounded-full border-2 border-muted-foreground/40 grid place-items-center">
                          {s.status === "running"
                            ? <Spinner size={10} className="animate-spin" />
                            : <span className="text-[9px] font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{s.label}</div>
                      <div className="text-xs text-muted-foreground truncate">→ {s.deliverable_label}</div>
                      {isFailed && s.error ? (
                        <div className="text-[11px] text-rose-500 mt-1 truncate">{s.error}</div>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="lg:col-span-8">
          <div className="text-overline text-muted-foreground mb-3">Deliverable</div>
          {!active ? (
            <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="deliverable-empty">
              <FileText size={32} className="text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">
                {run.status === "completed"
                  ? "Select a step on the left to view its deliverable."
                  : "Deliverables will appear here as the workflow runs."}
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-6" data-testid="deliverable-view">
              <div className="mb-4">
                <div className="font-display text-xl tracking-tight">{active.label}</div>
                {active.summary && (
                  <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl leading-relaxed">{active.summary}</p>
                )}
              </div>
              <DeliverableRender content={active.content} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
